import React, { useEffect, useMemo } from 'react';
import { Flex, Center, Box, Text } from '@chakra-ui/react';
import AssistantMessage from './AssistantMessage';
import { useSelector } from 'src/store';
import Input from './Input';
import ProgramSelector from './ProgramSelector';
import Messages from './Messages';
import { getMessageList } from 'src/api/assistant/query';
import UserMessage from './UserMessage';
import { BASE_URL } from 'src/configs/config';
import { PropagateLoader } from 'react-spinners';
import { useQuery } from 'react-query';
import { useIntl } from 'react-intl';
import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport, UIMessage } from 'ai';
import { getMessageText } from 'src/api/assistant/types';

const sections: Record<string, string> = {
  preparation: 'Preparation',
  affirmation: 'Affirmation',
  gate: 'Portals',
  communication: 'Communication',
  frequency: 'Frequency',
};

const AssistantChat: React.FC = () => {
  const { formatMessage } = useIntl();
  const user = useSelector((state) => state.auth.user);
  const { focusProgram, isChatOpen } = useSelector((state) => ({
    focusProgram: state.assistant.focusProgram,
    isChatOpen: state.assistant.isChatOpen,
  }));

  const focusProgramSection = focusProgram
    ? sections[focusProgram] ?? focusProgram
    : '';

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: `${BASE_URL}/sal-assistant/chat`,
        headers: () => ({
          authorization: `Bearer ${window.localStorage.getItem('__sal_auth') ?? ''}`,
          'x-localization': window.localStorage.getItem('locale') ?? 'en',
        }),
      }),
    [],
  );

  const {
    data: chatHistory,
    isLoading: isHistoryLoading,
    error: historyError,
  } = useQuery<UIMessage[], Error>(...getMessageList());

  const { messages, sendMessage, status, error, setMessages } = useChat({
    transport,
  });

  useEffect(() => {
    if (historyError) {
      console.error('Error fetching chat history:', historyError);
    }
  }, [historyError]);

  useEffect(() => {
    if (chatHistory?.length) {
      setMessages(chatHistory);
    }
  }, [chatHistory, setMessages]);

  const isInputDisabled =
    status === 'submitted' || status === 'streaming' || !focusProgramSection;

  const send = async (message: string): Promise<void> => {
    if (!message.trim() || !focusProgramSection || isInputDisabled) {
      return;
    }

    await sendMessage(
      { text: message },
      {
        body: {
          focusProgram: focusProgramSection,
        },
      },
    );
  };

  const renderedMessages = messages.map((message) => {
    const text = getMessageText(message);
    const isStreamingAssistant =
      message.role === 'assistant' &&
      (status === 'streaming' || status === 'submitted') &&
      message.id === messages[messages.length - 1]?.id;

    return message.role === 'user' ? (
      <UserMessage key={message.id} text={text} />
    ) : (
      <AssistantMessage
        key={message.id}
        text={text}
        isStreaming={isStreamingAssistant}
      />
    );
  });

  return (
    <Flex
      flexDir="column"
      position="absolute"
      top={0}
      right={0}
      width="100%"
      height="100%"
      bg="white"
      transform={`translateX(${isChatOpen ? '0' : '100%'})`}
      transition="transform 0.3s ease-in-out"
      p={2}
      visibility={isChatOpen ? 'visible' : 'hidden'}
    >
      <Box overflowY="auto" flex="1">
        {!chatHistory?.length && !focusProgram && isChatOpen && (
          <AssistantMessage
            text={
              formatMessage({ id: 'sal_assistant.user_welcome_message' }) +
              (user?.name?.split(' ')[0] ?? '') +
              '?'
            }
          />
        )}
        {isHistoryLoading ? (
          <Center h="100%">
            <PropagateLoader color="#2895FF" />
          </Center>
        ) : (
          <Messages messages={renderedMessages} />
        )}
        {(error || historyError) && (
          <Text color="red.500" fontSize="sm" mt={2} px={2}>
            {error?.message ?? historyError?.message}
          </Text>
        )}
      </Box>
      {focusProgram ? (
        <Input onSend={send} isDisabled={isInputDisabled} />
      ) : (
        <ProgramSelector />
      )}
    </Flex>
  );
};

export default AssistantChat;
